import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * A game's feature set follows the plan of whoever owns it, not the plan of the
 * collaborator doing the looking. A distributor on the Free plan managing a
 * Premium owner's game should see everything the owner sees, and a Premium
 * manager should not be capped by their own player limit inside an Enterprise
 * owner's game.
 *
 * Returns the membership object that should drive every membershipRestrictions
 * and testMemberRestrictions check inside a managed game. When the viewer is
 * the owner, or when the owner's tier is unknown (outside a managed game, or
 * the lookup failed), this is the viewer's own membership — i.e. the behavior
 * before owner-tier resolution existed.
 *
 * Only `.membership` is ever read off this object anywhere in the app, but the
 * viewer's own object is spread so the shape stays intact for any field added
 * later.
 *
 * @returns {object|null} the membership object to gate features with
 */
export function useEffectiveMembership() {
  const membership = useSelector(state => state.userData.membership);
  const gameOwnerMembership = useSelector(state => state.userData.gameOwnerMembership);

  return useMemo(() => {
    if (typeof gameOwnerMembership !== 'number') return membership;
    if (!membership) return { 'membership': gameOwnerMembership };
    return { ...membership, 'membership': gameOwnerMembership };
  }, [membership, gameOwnerMembership]);
}
